// Audit jurnali — xsib-core `platform.audit` moduli bilan HAQIQIY kontrakt
// (koordinator tasdiqlagan — avvalgi taxmin ESKIRGAN edi, quyida tuzatilgan):
//
// GET /api/audit?action&actorType&targetType&targetId&from&to&page&size
//   → {rows:[{id,at,actorType,actorId,action,targetType,targetId,result,message,detail,ip}],
//      page, size, hasNext}
//
// ⚠️ `total` YO'Q — faqat `hasNext` (`shared/api/pageResult.ts`ga qarang,
// u yerda antd `Pagination` bilan yarashtirish uchun sun'iy `total` yasaladi).
// ⚠️ `from`/`to` MAJBURIY.
// ⚠️ Aktor va nishon ENDI "login" emas, "obyekt turi + id"
// (`actorType/actorId`, `targetType/targetId`). Eskirgan taxminimda
// "abonent login bo'yicha erkin qidiruv" bor deb yozilgan edi — bunday
// parametr yo'q, shuning uchun qidiruv maydoni OLIB TASHLANDI (`AuditPage`),
// faqat sana oralig'i qoladi.
// ⚠️ `status` emas — maydon nomi `result`.
// ⚠️ `id` — BIGINT (son), `detail` — JSONB obyekt, `message` NULL bo'lishi
// mumkin. Manba: `platform/api/AuditEntryView.java` va
// `V1_001__platform_init.sql` dagi `platform.audit_log` ta'rifi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { OPERATION_STATUSES, type OperationStatus } from '@/shared/api/operationResult'
import { syntheticTotal } from '@/shared/api/pageResult'
import type { Translate } from '@/shared/api/errors'
import { instantSchema } from '@/shared/api/schemas'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'
import { toDetailFields, type AuditDetailField } from './auditDetail'

// ⚠️ Qayta eksport — chaqiruvchilar (`AuditPage`) bitta manzildan olsin
export { toDetailFields }
export type { AuditDetailField }

/**
 * ⚠️ `id` — SON, UUID EMAS (`platform.audit_log.id` = `BIGINT GENERATED
 * ALWAYS AS IDENTITY`, `AuditEntryView.id` = `long`). Bu yerda avval
 * `uuidSchema` turgan va zod HAR javobni rad etardi — natijada audit
 * sahifasi mangu skeletonda qolar, xato ham ko'rinmasdi (react-query
 * qayta urinishlar orasida "pending" holatida). Ro'yxat kaliti satr
 * bo'lishi qulay, shuning uchun `toAuditEntry` da satrga o'giriladi.
 *
 * ⚠️ `detail` — JSONB OBYEKT (`Map<String, Object>`), satr emas.
 *
 * ⚠️ `message` — bazada NULL bo'lishi mumkin (`message TEXT`, NOT NULL yo'q).
 */
const auditEntrySchema = z.object({
  id: z.union([z.number(), z.string()]),
  at: instantSchema,
  actorType: z.string(),
  actorId: z.string().nullish(),
  action: z.string(),
  targetType: z.string().nullish(),
  targetId: z.string().nullish(),
  result: z.enum(OPERATION_STATUSES),
  message: z.string().nullish(),
  detail: z.unknown().nullish(),
  ip: z.string().nullish(),
})

/**
 * `detail` ning XOM ko'rinishi — texnik blok uchun.
 *
 * ⚠️ Bu matn ENDI BIRINCHI KO'RINISH EMAS (BUG-044). U yopiq
 * `Collapse` ichida turadi, ekranga esa {@link toDetailFields} yasagan
 * yorliqli ro'yxat chiqadi. Xom shakl OLIB TASHLANMADI: nosozlik
 * qidirilayotganda aynan u kerak bo'ladi va tuzilma har amalda boshqa
 * ({@code AuditEntryView.detail} javadoc'i) — ya'ni yorliqli ro'yxat
 * hech qachon 100% qamramaydi.
 */
function toDetailText(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'string') return raw.trim() === '' ? null : raw
  try {
    const text = JSON.stringify(raw, null, 2)
    return text === undefined || text === '{}' ? null : text
  } catch {
    return null
  }
}

/**
 * Jurnal qatorining matni — PANEL TILIDA (N5).
 *
 * <h2>Nima o'lchangan (2026-08-15, jonli, tenant `af-isp`, tili `ru`)</h2>
 *
 * `/audit` da 25 o'rinda server yozgan o'zbekcha matn ko'rindi
 * («TOTP tasdiqlandi», «TOTP kodi noto'g'ri», «Ledger sog'lom: …»),
 * yonidagi ustunlar esa ruscha. Til almashtirilganda o'zgarmasdi.
 *
 * <h2>⚠️ NEGA BU YERDA, `job` YO'LIDAGIDEK USTUN QO'SHILMASDAN</h2>
 *
 * Job yo'lida (`jobMessageText`) bazaga YANGI ustun qo'shildi, chunki
 * `platform.job` da tarjima uchun ilinadigan KOD umuman yo'q edi.
 * Bu yerda esa u ALLAQACHON bor va javobda keladi — `action`
 * (`ROLE_CREATE`, `TOTP_VERIFY`, …). Xabar matni deyarli har doim
 * o'sha kodning qayta aytilishi: `ROLE_CREATE` → «Rol yaratildi».
 *
 * ⚠️ Ikkinchi sabab arxitektura chegarasi: audit yozuv yo'li
 * `uz.xsib.common.outbox.AuditEvent` dan o'tadi va u 149 chaqiruv
 * joyi bilan birga BOSHQA ish doirasida (`ISH-NAVBATI.md`) — ikki
 * tomondan tegish yo'qoladigan tuzatish demakdir.
 *
 * <h2>⚠️ TARJIMA FAQAT KALIT MAVJUD BO'LGANDA</h2>
 *
 * Bu qoidaning butun mazmuni MA'LUMOT YO'QOTMASLIKDA. Ba'zi
 * xabarlar `action` da yo'q qo'shimcha faktni olib yuradi
 * («Ledger sog'lom: 474 hisob, 3690 yozuv tekshirildi»). Bunday
 * amalga kalit ATAYLAB yozilmaydi va qator serverning matni bilan
 * qoladi: yarim tarjima qilib faktni yo'qotish — nuqsonni
 * TUZATMAY, KATTALASHTIRISH bo'lardi.
 *
 * Kalit qo'shish, ya'ni «bu xabar `action` dan boshqa hech narsa
 * demaydi» degan qaror — har amal uchun ALOHIDA va ONGLI.
 *
 * ⚠️ Natija bo'yicha kalit (`audit.message.<AMAL>.<NATIJA>`) BIRINCHI
 * qaraladi: bir amal muvaffaqiyat va xatoda butunlay boshqa narsa
 * aytadi («TOTP tasdiqlandi» / «TOTP kodi noto'g'ri»).
 */
export function auditMessageText(entry: AuditEntry, t: Translate): string | null {
  const byResult = t(`audit.message.${entry.action}.${entry.result}`, { defaultValue: '' })
  if (byResult !== '') return byResult
  const byAction = t(`audit.message.${entry.action}`, { defaultValue: '' })
  if (byAction !== '') return byAction
  // Server text is Uzbek only; in other languages the action label and detail fields speak instead.
  return t('common.langCode', { defaultValue: '' }) === 'uz' && entry.message !== '' ? entry.message : null
}

export interface AuditEntry {
  id: string
  at: string
  actorType: string
  actorId: string | null
  action: string
  targetType: string | null
  targetId: string | null
  result: OperationStatus
  message: string
  /** Xom JSON — faqat texnik blok uchun (`toDetailText` javadoc'i) */
  detail: string | null
  /** Yorliqli qatorlar — ekranga SHU chiqadi (`toDetailFields` javadoc'i) */
  detailFields: AuditDetailField[]
  ip: string | null
}

function toAuditEntry(raw: z.infer<typeof auditEntrySchema>): AuditEntry {
  return {
    id: String(raw.id),
    at: raw.at,
    actorType: raw.actorType,
    actorId: raw.actorId ?? null,
    action: raw.action,
    targetType: raw.targetType ?? null,
    targetId: raw.targetId ?? null,
    result: raw.result,
    message: raw.message ?? '',
    detail: toDetailText(raw.detail),
    detailFields: toDetailFields(raw.detail),
    ip: raw.ip ?? null,
  }
}

const auditPageSchema = z.object({
  rows: z.array(auditEntrySchema),
  page: z.number(),
  size: z.number(),
  hasNext: z.boolean(),
})

export interface AuditRangeFilter {
  /** ISO-8601 UTC — MAJBURIY */
  from: string
  to: string
}

/**
 * Audit sahifasini oladi. Sana oralig'i YOPILGAN qiymat sifatida beriladi
 * (`AuditPage` shu funksiyani `range` o'zgarganda `useMemo` bilan qayta
 * yaratadi — kesh kaliti ham oraliqqa bog'liq).
 */
export function fetchAuditPage(
  range: AuditRangeFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<AuditEntry>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/audit', {
      signal,
      params: {
        from: range.from,
        to: range.to,
        // Backend sahifani 0 dan sanaydi — boshqa ro'yxat endpointlari bilan bir xil andoza
        page: state.page - 1,
        size: state.pageSize,
      },
    })
    const parsed = auditPageSchema.parse(response.data)
    return {
      items: parsed.rows.map(toAuditEntry),
      total: syntheticTotal(state.page, state.pageSize, parsed.rows.length, parsed.hasNext),
    }
  }
}
